// CTV-863: add one of the station's videos to the Materials tab.
//
// A manager (or CTVBuyer, as super_admin) picks an mp4 and names it. The
// file goes straight to Supabase Storage on a signed URL from the API, then
// the API records it on the station row (the same two-step as FramePicker).
// The upload is an XMLHttpRequest rather than fetch, for the one thing fetch
// cannot give: progress. A video is tens of megabytes, and a button that
// says "Saving…" for a minute reads as stuck.
//
// Laid out like FramePicker's Upload a Picture pane (feedback 2026-09-10):
// the file is chosen through a dashed drop-zone button that then shows the
// video, never the browser's own "Choose file" control.

import React from 'react';
import { Check, Film, Loader2, X } from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import type { MaterialsVideo } from '../../../types/selfService';

const VIDEO_MIME = 'video/mp4';
/** Matches MAX_VIDEO_BYTES in the API, which mirrors the bucket's own cap. */
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const MAX_NAME = 60;
/** Matches MAX_VIDEO_DESCRIPTION in the API. */
export const MAX_DESCRIPTION = 300;

export interface AddVideoDialogProps {
  slug: string;
  onSaved: (videos: MaterialsVideo[], saved: MaterialsVideo) => void;
  onCancel: () => void;
}

/** PUT the file to the signed URL, reporting bytes sent as it goes. */
function putWithProgress(
  url: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('content-type', VIDEO_MIME);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('upload failed'));
    xhr.send(file);
  });
}

/** "Streamline_KEZI_30s.mp4" → "Streamline KEZI 30s", a name to start from. */
function nameFromFile(file: File): string {
  return file.name
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .slice(0, MAX_NAME);
}

const megabytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const AddVideoDialog: React.FC<AddVideoDialogProps> = ({ slug, onSaved, onCancel }) => {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // The chosen file, playable in the zone so the uploader can check it is
  // the right cut before saving. Released when it changes or the box closes.
  const previewUrl = React.useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  React.useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const pick = (picked: File | null | undefined) => {
    setFileError(null);
    setSaveError(null);
    if (!picked) return;
    // The type check is on the file's own type, so a renamed .mov is caught
    // here rather than by the API a moment later.
    if (picked.type !== VIDEO_MIME) {
      setFile(null);
      setFileError('Only MP4 video can be added. Export the video as an .mp4 and try again.');
      return;
    }
    if (picked.size > MAX_VIDEO_BYTES) {
      setFile(null);
      setFileError(`That file is over ${MAX_VIDEO_BYTES / 1024 / 1024} MB. Export a smaller one.`);
      return;
    }
    setFile(picked);
    if (!name.trim()) setName(nameFromFile(picked));
  };

  const save = async () => {
    if (!file || !name.trim() || saving) return;
    setSaving(true);
    setSaveError(null);
    setProgress(0);
    try {
      const ticket = await apiClient.post<{ uploadUrl: string; storagePath: string }>(
        `/stations/${encodeURIComponent(slug)}/materials-videos/upload-url`,
        { mimeType: VIDEO_MIME, sizeBytes: file.size },
      );
      await putWithProgress(ticket.uploadUrl, file, setProgress);
      const result = await apiClient.post<{ videos: MaterialsVideo[]; video: MaterialsVideo }>(
        `/stations/${encodeURIComponent(slug)}/materials-videos`,
        { name: name.trim(), description: description.trim(), storagePath: ticket.storagePath },
      );
      onSaved(result.videos, result.video);
    } catch (e) {
      // The API's own words for the cap ("A station can keep 10 videos…")
      // and the type refusal are worth showing; anything else gets one line.
      setSaveError(
        e instanceof Error && /keep \d+ videos|\.mp4/.test(e.message)
          ? e.message
          : "Couldn't save the video. Try again.",
      );
      setSaving(false);
      setProgress(null);
    }
  };

  const canSave = !!file && name.trim().length > 0 && !saving;

  // Centred in the main area, not the window: the backdrop starts past the
  // admin sidebar (w-60 in PublisherAdminLayout), as FramePicker's does.
  return (
    <div
      className="fixed inset-y-0 left-60 right-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add a video"
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
      >
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3">
          <div className="mr-auto">
            <div className="text-[15px] font-semibold text-slate-900">Add a Video</div>
            <div className="text-[13px] text-slate-500">
              An MP4 made for this station. The team can watch and download it from the Videos tab.
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          className="flex flex-col gap-4 overflow-y-auto p-5"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="video/mp4,.mp4"
            aria-label="Video file"
            className="sr-only"
            disabled={saving}
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={saving}
            className="flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-[13px] text-slate-600 transition-colors hover:border-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:hover:border-slate-300 disabled:hover:bg-slate-50"
          >
            {file && previewUrl ? (
              <video
                src={previewUrl}
                muted
                playsInline
                preload="metadata"
                data-testid="video-preview"
                className="max-h-52 rounded-md bg-black shadow-sm"
              />
            ) : (
              <Film className="h-6 w-6 text-slate-400" aria-hidden />
            )}
            <span className="font-medium">{file ? file.name : 'Choose a Video'}</span>
            <span className="text-[12px] text-slate-500">
              {file
                ? `${megabytes(file.size)}. Click to choose a different one.`
                : `MP4, up to ${MAX_VIDEO_BYTES / 1024 / 1024} MB.`}
            </span>
          </button>
          {fileError && (
            <p role="alert" className="text-[13px] text-red-700">
              {fileError}
            </p>
          )}

          <label className="flex flex-col gap-1 text-[13px]">
            <span className="font-medium text-slate-700">Name this video</span>
            <input
              type="text"
              value={name}
              maxLength={MAX_NAME}
              disabled={saving}
              onChange={(e) => setName(e.target.value)}
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-[13px]">
            <span className="font-medium text-slate-700">
              Description <span className="font-normal text-slate-400">(optional)</span>
            </span>
            <textarea
              value={description}
              maxLength={MAX_DESCRIPTION}
              rows={2}
              disabled={saving}
              onChange={(e) => setDescription(e.target.value)}
              className="resize-y rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
            />
            <span className="text-[12px] text-slate-500">
              Shown under the video's name, so the team knows what it is and who it is for.
            </span>
          </label>

          {progress !== null && (
            <div aria-live="polite" className="flex flex-col gap-1 text-[12px] text-slate-500">
              <span>{progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : 'Saving…'}</span>
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
                className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
              >
                <div className="h-full bg-blue-600" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>
          )}
          {saveError && (
            <p role="alert" className="text-[13px] text-red-700">
              {saveError}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded-md px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50 disabled:text-slate-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {saving ? 'Saving…' : 'Save Video'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVideoDialog;
